import React, { useRef, useState, useCallback, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Play } from 'lucide-react-native';
import { Serie } from '@/types';
import { CachedSerieImage } from '@/components/CachedSerieImage';
import { SerieCardHoverOverlay } from '@/components/SerieCardHoverOverlay';
import { useTheme } from '@/hooks/useTheme';
import { useBranding } from '@/contexts/BrandingContext';

const HOVER_DELAY = 500;
const HOVER_EXIT_DELAY = 300;

interface SerieCardWebProps {
  serie: Serie;
  cardWidth: number;
  onPress: () => void;
  style?: any;
  t: (key: any) => string;
}

const injectHoverStyles = () => {
  if (document.getElementById('serie-hover-styles')) return;
  const styleEl = document.createElement('style');
  styleEl.id = 'serie-hover-styles';
  styleEl.textContent = `
    @keyframes hoverZoomIn {
      from { opacity: 0; transform: scale(0.92); }
      to { opacity: 1; transform: scale(1); }
    }
  `;
  document.head.appendChild(styleEl);
};

const getOrCreatePortalRoot = (): HTMLElement => {
  injectHoverStyles();
  let root = document.getElementById('hover-portal-root');
  if (!root) {
    root = document.createElement('div');
    root.id = 'hover-portal-root';
    root.style.position = 'fixed';
    root.style.top = '0';
    root.style.left = '0';
    root.style.width = '0';
    root.style.height = '0';
    root.style.overflow = 'visible';
    root.style.zIndex = '99999';
    root.style.pointerEvents = 'none';
    document.body.appendChild(root);
  }
  return root;
};

export const SerieCardWeb: React.FC<SerieCardWebProps> = ({
  serie,
  cardWidth,
  onPress,
  style,
  t,
}) => {
  const { colors: { primary: primaryColor } } = useTheme();
  const { uiOverrides } = useBranding();
  const [isHovered, setIsHovered] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayPosition, setOverlayPosition] = useState<{ top: number; left: number } | null>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const cardRef = useRef<View>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const overlayWidth = cardWidth * 1.5;

  const dismissOverlay = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    setShowOverlay(false);
    setOverlayPosition(null);
    setIsHovered(false);
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web') {
      setPortalContainer(getOrCreatePortalRoot());
    }
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || !showOverlay) return;

    const handleWheel = () => {
      dismissOverlay();
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [showOverlay, dismissOverlay]);

  const calculatePosition = useCallback(() => {
    if (!cardRef.current) return;
    const node = cardRef.current as any;
    if (node && typeof node.getBoundingClientRect === 'function') {
      const rect = node.getBoundingClientRect();
      const screenW = window.innerWidth;
      const screenH = window.innerHeight;

      let left = rect.left - (overlayWidth - cardWidth) / 2;
      if (left < 8) left = 8;
      if (left + overlayWidth > screenW - 8) left = screenW - 8 - overlayWidth;

      const overlayContentWidth = cardWidth * 1.45;
      const overlayImageHeight = overlayContentWidth * (3 / 2);
      const infoPanelHeight = 120;
      const estimatedOverlayHeight = overlayImageHeight + infoPanelHeight;

      let top = rect.top;
      if (top + estimatedOverlayHeight > screenH - 8) {
        top = screenH - 8 - estimatedOverlayHeight;
      }
      if (top < 8) top = 8;

      setOverlayPosition({ top, left });
    }
  }, [cardWidth, overlayWidth]);

  const handleMouseEnter = useCallback(() => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    setIsHovered(true);
    hoverTimerRef.current = setTimeout(() => {
      calculatePosition();
      setShowOverlay(true);
    }, HOVER_DELAY);
  }, [calculatePosition]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setIsHovered(false);
    exitTimerRef.current = setTimeout(() => {
      setShowOverlay(false);
      setOverlayPosition(null);
    }, HOVER_EXIT_DELAY);
  }, []);

  const handleOverlayMouseEnter = useCallback(() => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
  }, []);

  const handleOverlayMouseLeave = useCallback(() => {
    exitTimerRef.current = setTimeout(() => {
      setShowOverlay(false);
      setOverlayPosition(null);
      setIsHovered(false);
    }, HOVER_EXIT_DELAY);
  }, []);

  const landscapeMultiplier = Platform.OS === 'web' ? 1.2 * 1.3 : 1.2;
  const effectiveWidth = serie.thumbnailLandscape ? cardWidth * landscapeMultiplier : cardWidth;

  if (Platform.OS !== 'web') {
    return (
      <TouchableOpacity style={style} onPress={onPress}>
        <View style={[styles.imageContainer, { width: cardWidth, height: cardWidth * 1.5 }]}>
          <CachedSerieImage thumbnailUrl={serie.thumbnail} seriesId={serie.id} style={styles.image} accessibilityLabel={serie.title} />
          <View style={styles.playIconOverlay}>
            <Play size={16} color="#ffffff" fill="#ffffff" />
          </View>
        </View>
        <View style={styles.cardContent}>
          <Text style={styles.cardTitle} numberOfLines={2}>{serie.title}</Text>
        </View>
      </TouchableOpacity>
    );
  }

  const ReactDOM = require('react-dom');

  const overlayElement = showOverlay && overlayPosition ? (
    <div
      style={{
        position: 'fixed',
        top: overlayPosition.top,
        left: overlayPosition.left,
        zIndex: 99999,
        pointerEvents: 'auto',
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(0,0,0,0.7), 0 4px 12px rgba(0,0,0,0.5)',
        transform: 'scale(1)',
        animation: 'hoverZoomIn 200ms ease forwards',
      }}
      onMouseEnter={handleOverlayMouseEnter}
      onMouseLeave={handleOverlayMouseLeave}
    >
      <SerieCardHoverOverlay
        serie={serie}
        onPlay={() => { dismissOverlay(); onPress(); }}
        cardWidth={cardWidth}
        t={t}
      />
    </div>
  ) : null;

  return (
    <>
      <View
        ref={cardRef}
        style={[style, { width: effectiveWidth }]}
        // @ts-ignore
        onMouseEnter={handleMouseEnter}
        // @ts-ignore
        onMouseLeave={handleMouseLeave}
      >
        <TouchableOpacity onPress={onPress} activeOpacity={0.9}>
          <View
            style={[
              styles.imageContainer,
              {
                width: effectiveWidth,
                aspectRatio: serie.thumbnailLandscape ? 16 / 9 : 2 / 3,
                // @ts-ignore
                transition: 'transform 200ms ease, box-shadow 200ms ease',
                transform: isHovered && !showOverlay ? 'scale(1.05)' : 'scale(1)',
              },
            ]}
          >
            <CachedSerieImage thumbnailUrl={serie.thumbnailLandscape || serie.thumbnail} seriesId={serie.id} style={styles.image} accessibilityLabel={serie.title} />
            {serie.isNew && (
              <View style={[styles.badge, styles.newBadge, uiOverrides.badge_new_color ? { backgroundColor: uiOverrides.badge_new_color } : undefined]}>
                <Text style={styles.badgeText}>{t('new')}</Text>
              </View>
            )}
            {serie.isTrending && (
              <View style={[styles.badge, styles.trendingBadge, { backgroundColor: uiOverrides.badge_trending_color || primaryColor }]}>
                <Text style={styles.badgeText}>{t('trending')}</Text>
              </View>
            )}
            <View style={styles.playIconOverlay}>
              <Play size={16} color="#ffffff" fill="#ffffff" />
            </View>
          </View>
          <View style={styles.cardContent}>
            <Text style={styles.cardTitle} numberOfLines={2}>{serie.title}</Text>
          </View>
        </TouchableOpacity>
      </View>

      {portalContainer && overlayElement && ReactDOM.createPortal(overlayElement, portalContainer)}
    </>
  );
};

const styles = StyleSheet.create({
  imageContainer: {
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  badge: {
    position: 'absolute',
    top: 8,
    left: 8,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  newBadge: {
    backgroundColor: '#00D4AA',
  },
  trendingBadge: {},
  badgeText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '600',
    lineHeight: 14,
  },
  playIconOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: -20,
    marginTop: -20,
  },
  cardContent: {
    paddingHorizontal: 4,
    paddingTop: 8,
    height: 44,
    overflow: 'hidden',
  },
  cardTitle: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 16,
  },
});
